/**
 * Content for `/…-alternative` marketing pages.
 *
 * Each page must read as its own article for SEO — unique reasons, comparison
 * rows, migration steps, section headlines, and FAQs. Comparison copy on the
 * right describes a *category* (not a rival's current feature sheet), because
 * indexed claims about someone else's product go stale on their own.
 *
 * Shared UI lives in `CompetitorAlternativePage`.
 */

import { FREE_USER_STARTING_CREDITS_DISPLAY } from '@/constants/config';

/**
 * @typedef {object} AlternativePage
 * @property {string} slug
 * @property {string} competitor
 * @property {string} title
 * @property {string} description
 * @property {string} keywords
 * @property {string} breadcrumbLabel
 * @property {string} mastheadLabel
 * @property {string} hubSummary One line for the `/alternatives` index card.
 * @property {{ line1: string, beforeAccent: string, accent: string, afterAccent: string }} headline
 * @property {string} lede
 * @property {string} importCta
 * @property {string} [importHref] Hero secondary CTA. Defaults to `/switch`.
 * @property {string} creditNote
 * @property {string} whySwitchLabel
 * @property {string} whyKicker
 * @property {string[][]} whyHeading
 * @property {string} whyLede
 * @property {string} compareOtherLabel
 * @property {string} compareSectionLabel
 * @property {string} compareKicker
 * @property {string[][]} compareHeading
 * @property {string} compareLede
 * @property {string} migrateSectionLabel
 * @property {string} migrateKicker
 * @property {string[][]} migrateHeading
 * @property {string} migrateLede
 * @property {string} migrateCta
 * @property {string} [migrateHref] Migration-section CTA. Defaults to `/switch`.
 * @property {string} [docsCta] Extra FAQ-column link to API docs, when set.
 * @property {string} [docsHref]
 * @property {string} faqSectionLabel
 * @property {string} faqKicker
 * @property {string[][]} faqHeading
 * @property {string} faqLede
 * @property {string} howtoName
 * @property {string[]} marquee
 * @property {object[]} reasons
 * @property {object[]} comparison
 * @property {object[]} steps
 * @property {{ q: string, a: string }[]} faqs
 */

/** @type {AlternativePage} */
export const CHATGPT_ALTERNATIVE = {
  slug: '/chatgpt-alternative',
  competitor: 'ChatGPT',
  title: 'ChatGPT Alternative — Every Major Super Intelligence Model, One App',
  description: 'A free ChatGPT alternative: GPT, Claude, Gemini, and Arya in one private workspace, plus images, video, music, and an API. Import your ChatGPT history.',
  keywords: 'ChatGPT alternative, alternative to ChatGPT, free ChatGPT alternative, Super Intelligence like ChatGPT, apps like ChatGPT, sites like ChatGPT, ChatGPT competitor, best ChatGPT alternative',
  breadcrumbLabel: 'ChatGPT alternative',
  mastheadLabel: 'Gab AI as a ChatGPT alternative',
  hubSummary: 'The assistant most people arrive from. One company’s lineup, and a history you can bring with you.',
  headline: {
    line1: 'A ChatGPT alternative',
    beforeAccent: 'that is ',
    accent: 'not one model',
    afterAccent: '.',
  },
  lede: 'Most alternatives to ChatGPT are another single assistant from another single company. Gab AI puts GPT, Claude, Gemini, and Arya in one picker — with images, video, music, code, and an API on the same account, and your identity masked from every provider behind it.',
  importCta: 'Import your ChatGPT history',
  creditNote: `${FREE_USER_STARTING_CREDITS_DISPLAY} credits to start · No credit card required`,
  whySwitchLabel: 'Why people switch from ChatGPT',
  whyKicker: 'Why switch',
  whyHeading: [['Four reasons people'], ['move over.']],
  whyLede: 'None of them is that one model is smarter than another this week. They are the things that do not change when the next release lands.',
  compareOtherLabel: 'A single-model assistant',
  compareSectionLabel: 'Gab AI compared with a single-model assistant',
  compareKicker: 'Side by side',
  compareHeading: [['One account, or'], ['five subscriptions.']],
  compareLede: 'The right-hand column describes the category rather than a named product: rival assistants ship changes weekly, and a comparison that claims what one of them does today goes stale on its own. Everything on the left is a promise about our side of it.',
  migrateSectionLabel: 'Bringing your history across',
  migrateKicker: 'Moving over',
  migrateHeading: [['Switching costs'], ['you one file.']],
  migrateLede: 'The reason people stay somewhere they have outgrown is usually the archive, not the product. Bring the archive.',
  migrateCta: 'How importing works',
  faqSectionLabel: 'ChatGPT alternative questions',
  faqKicker: 'Answers',
  faqHeading: [['Before you'], ['switch.']],
  faqLede: 'Compare the plans, read how the import works, or just start a chat and see whether the answers are better.',
  howtoName: 'Move your ChatGPT history to Gab AI',
  marquee: [
    'GPT', 'Claude', 'Gemini', 'Arya', 'Images', 'Video', 'Music',
    'Voice', 'Code', 'Files', 'Agents', 'Tasks', 'Memory', 'API',
  ],
  reasons: [
    {
      key: 'models',
      icon: 'layer-group',
      kicker: 'Model choice',
      title: 'Not one company’s lineup',
      copy: 'GPT, Claude, Gemini, and Arya sit in the same picker. Ask the same question of two of them, keep the better answer, and never pay five subscriptions to have the option.',
      chips: ['Per-message switching', 'Automatic routing', 'Side-by-side compare'],
      href: '/models',
      cta: 'See every model',
    },
    {
      key: 'scope',
      icon: 'wand-magic-sparkles',
      kicker: 'One account',
      title: 'Chat is where it starts',
      copy: 'Images, video, music, voice, code, documents, and file analysis happen in the same conversation and draw on the same balance — not in four other products with four other bills.',
      chips: ['Images & video', 'Music & voice', 'Docs, slides & sheets'],
      href: '/features',
      cta: 'See all features',
    },
    {
      key: 'privacy',
      icon: 'user-secret',
      kicker: 'Privacy',
      title: 'Anonymous to the model',
      copy: 'Your conversations, files, and creations are never used to train our model, and requests sent to third-party providers are anonymized so those providers never learn who is asking.',
      chips: ['No training on your chats', 'Identity masked', 'Full data export'],
      href: '/docs/privacy',
      cta: 'Read the privacy docs',
    },
    {
      key: 'answers',
      icon: 'comments',
      kicker: 'Straight answers',
      title: 'Unfiltered, not lectured',
      copy: 'Arya is Gab AI’s own model, and it answers political, cultural, and religious questions directly instead of deflecting. No ideological guardrails bolted on top, and no refusal where an answer would do.',
      chips: ['Uncensored by design', 'Answers, not hedges', 'Gab AI’s own model'],
      href: '/right-wing-ai',
      cta: 'Meet Arya',
    },
  ],
  comparison: [
    {
      label: 'Which models you can use',
      gab: 'GPT, Claude, Gemini, and Arya from one picker',
      other: 'Whichever models that one provider ships',
    },
    {
      label: 'Images, video, and music',
      gab: 'Generated in the same chat, from the same balance',
      other: 'Usually a separate product with a separate bill',
    },
    {
      label: 'Your existing history',
      gab: 'Import your ChatGPT, Claude, or Grok export',
      other: 'A new account starts empty',
    },
    {
      label: 'Training on your conversations',
      gab: 'Never',
      other: 'Depends on the plan and your settings',
    },
    {
      label: 'What the provider learns about you',
      gab: 'Nothing — your identity is masked on every request',
      other: 'You are the account making the request',
    },
    {
      label: 'Political and religious questions',
      gab: 'Arya answers them directly',
      other: 'Commonly a refusal or a hedge',
    },
    {
      label: 'Developer access',
      gab: 'OpenAI-compatible API on the account you already have',
      other: 'A second platform account, billed separately',
    },
  ],
  steps: [
    {
      icon: 'upload',
      title: 'Export from your old assistant',
      copy: 'ChatGPT, Claude, and Grok all publish a data export in their settings. Ask for it and you get a file back.',
    },
    {
      icon: 'arrows-rotate',
      title: 'Drop the file into Gab AI',
      copy: 'Upload it once. Threads are matched and de-duplicated, so importing the same export twice does not double anything.',
    },
    {
      icon: 'magnifying-glass',
      title: 'Carry on where you stopped',
      copy: 'Every conversation arrives with its title and timestamp intact, searchable alongside the work you do from here.',
    },
  ],
  faqs: [
    {
      q: 'Is there a free ChatGPT alternative?',
      a: `Yes. Gab AI is free to start: a new account comes with ${FREE_USER_STARTING_CREDITS_DISPLAY} credits and needs no credit card, and those credits work across every model and every medium — chat, images, video, and music included. Plus unlocks Work mode for multi-step jobs, monthly credits, and free messages on the default model.`,
    },
    {
      q: 'What is the best ChatGPT alternative?',
      a: 'It depends on what you were using ChatGPT for. If you only ever want OpenAI models, staying put is reasonable. If you want to pick between GPT, Claude, Gemini, and Arya per message, generate images and video in the same thread, keep your conversations out of training data, and get direct answers on subjects other assistants avoid, that is the gap Gab AI is built to fill.',
    },
    {
      q: 'Can I still use GPT models on Gab AI?',
      a: 'Yes. GPT models are in the picker alongside Claude, Gemini, and Arya. Switching to Gab AI means adding models, not giving one up — and requests to third-party providers are anonymized, so OpenAI does not learn who is asking.',
    },
    {
      q: 'Do I lose my ChatGPT history if I switch?',
      a: 'No. Export your data from ChatGPT and upload the file to Gab AI. Every thread arrives with its title and timestamp, searchable next to your new conversations. The same import works for Claude and Grok exports.',
    },
    {
      q: 'Is Gab AI just a ChatGPT wrapper?',
      a: 'No. Arya is Gab AI’s own model, trained by us. Gab AI also gives you access to third-party models including GPT, Claude, and Gemini, but those are separate, clearly labelled choices in the picker rather than something dressed up as our own.',
    },
    {
      q: 'Are there apps like ChatGPT for iPhone and Android?',
      a: 'Gab AI ships native apps for iPhone, iPad, Android, and Mac, plus the full workspace in any browser. One account keeps chats, memory, files, and credits in sync across all of them.',
    },
  ],
};

/** @type {AlternativePage} */
export const POE_ALTERNATIVE = {
  slug: '/poe-alternative',
  competitor: 'Poe',
  title: 'Poe Alternative — Every Major Model, One App',
  description: 'A Poe alternative with GPT, Claude, Gemini, and Arya in one private workspace — plus images, video, music, and an API.',
  keywords: 'Poe alternative, alternative to Poe, Poe Super Intelligence alternative, better than Poe, Poe competitor, apps like Poe, sites like Poe, free Poe alternative, best Poe alternative',
  breadcrumbLabel: 'Poe alternative',
  mastheadLabel: 'Gab AI as a Poe alternative',
  hubSummary: 'Bot marketplaces make sampling easy and finishing hard. What a workspace does that a directory cannot.',
  headline: {
    line1: 'A Poe alternative',
    beforeAccent: 'that is ',
    accent: 'more than a bot list',
    afterAccent: '.',
  },
  lede: 'Bot marketplaces make it easy to sample dozens of assistants — and just as easy to drown in them. Gab AI collapses that clutter into one private workspace: GPT, Claude, Gemini, and Arya on a single picker and credit balance, with images, video, music, and an API included rather than bolted on as separate bots.',
  importCta: 'Bring your old chats over',
  creditNote: `${FREE_USER_STARTING_CREDITS_DISPLAY} free credits · Card not required`,
  whySwitchLabel: 'Why people leave multi-bot hubs',
  whyKicker: 'Beyond bots',
  whyHeading: [['Stop collecting'], ['assistants.']],
  whyLede: 'A longer bot directory is not the same as a better place to work. These are the gaps a marketplace leaves open — and the ones Gab AI closes.',
  compareOtherLabel: 'A multi-bot chat hub',
  compareSectionLabel: 'Gab AI vs a multi-bot chat hub',
  compareKicker: 'The real difference',
  compareHeading: [['A workspace,'], ['not a catalog.']],
  compareLede: 'We describe the category on the right on purpose. Bot hubs change their lineup constantly; what matters is the shape of the product — a directory of third-party bots versus a private workspace you actually live in.',
  migrateSectionLabel: 'Keep the threads that matter',
  migrateKicker: 'Bring the archive',
  migrateHeading: [['Your history'], ['does not stay behind.']],
  migrateLede: 'Leaving a bot hub often means abandoning months of threads. Export from ChatGPT, Claude, or Grok and land them in Gab AI before you rebuild anything.',
  migrateCta: 'See how import works',
  faqSectionLabel: 'Questions about leaving Poe',
  faqKicker: 'FAQ',
  faqHeading: [['Still deciding'], ['on a hub?']],
  faqLede: 'If you outgrew a bot directory, start free on Gab AI and keep the models you already rely on — plus Arya.',
  howtoName: 'Import chat history when leaving a bot hub for Gab AI',
  marquee: [
    'Arya', 'GPT', 'Claude', 'Gemini', 'Agents', 'Images', 'Video',
    'Music', 'Voice', 'API', 'Memory', 'Tasks', 'Files', 'Routing',
  ],
  reasons: [
    {
      key: 'collapse',
      icon: 'layer-group',
      kicker: 'One picker',
      title: 'Models, not a bot zoo',
      copy: 'Instead of hunting through a marketplace of wrappers, you pick GPT, Claude, Gemini, or Arya the way you pick a tool — per message, with optional automatic routing when you would rather not choose.',
      chips: ['Clear model labels', 'Per-message switch', 'Auto routing'],
      href: '/models',
      cta: 'Browse the model list',
    },
    {
      key: 'owned',
      icon: 'comments',
      kicker: 'Our model',
      title: 'Arya is not a rented bot',
      copy: 'Most hubs only resell other companies’ models. Arya is trained by Gab AI, sits in the same picker, and answers political, cultural, and religious questions without the usual lecture.',
      chips: ['First-party model', 'Direct answers', 'Clearly labelled'],
      href: '/right-wing-ai',
      cta: 'Read about Arya',
    },
    {
      key: 'studio',
      icon: 'wand-magic-sparkles',
      kicker: 'Beyond chat',
      title: 'Media on the same balance',
      copy: 'Image, video, and music generation are not separate “bots” with separate meters. They live in the same conversation and spend from the same credits as chat.',
      chips: ['Images & video', 'Music generation', 'One credit pool'],
      href: '/features',
      cta: 'Explore the workspace',
    },
    {
      key: 'mask',
      icon: 'user-secret',
      kicker: 'Privacy',
      title: 'Providers never see your name',
      copy: 'We never train on your chats, and every third-party request is anonymized. The model companies behind GPT, Claude, and Gemini do not learn which Gab AI account is asking.',
      chips: ['No chat training', 'Identity masked', 'Export anytime'],
      href: '/docs/privacy',
      cta: 'Privacy details',
    },
  ],
  comparison: [
    {
      label: 'What you are really using',
      gab: 'A private workspace with first-party Arya plus major models',
      other: 'Primarily a catalog of third-party bots',
    },
    {
      label: 'How models are presented',
      gab: 'One picker with clear names and per-message control',
      other: 'A long list of bots that can blur together',
    },
    {
      label: 'Images, video, and music',
      gab: 'Native generators on the same credit balance',
      other: 'Often another bot or another product surface',
    },
    {
      label: 'Who owns a flagship model',
      gab: 'Arya is Gab AI’s — not a white-label of someone else',
      other: 'Usually none — everything is rented upstream',
    },
    {
      label: 'Privacy toward model providers',
      gab: 'Identity masked on every outbound request',
      other: 'You are typically the account talking to the bot',
    },
    {
      label: 'API access',
      gab: 'OpenAI-compatible API on the same login',
      other: 'Chat product and developer access rarely share a home',
    },
    {
      label: 'Moving old threads in',
      gab: 'Import ChatGPT, Claude, or Grok exports',
      other: 'Starting over is the common path',
    },
  ],
  steps: [
    {
      icon: 'upload',
      title: 'Grab an export from a real archive',
      copy: 'If your important threads still live in ChatGPT, Claude, or Grok, download their official export — that is the file Gab AI can read today.',
    },
    {
      icon: 'arrows-rotate',
      title: 'Upload once into Gab AI',
      copy: 'We match and de-duplicate threads, so re-uploading the same export does not clone your library.',
    },
    {
      icon: 'magnifying-glass',
      title: 'Search the old work beside the new',
      copy: 'Titles and timestamps survive the move, so the bot-hopping era stays searchable instead of lost in a closed tab.',
    },
  ],
  faqs: [
    {
      q: 'Is there a free Poe alternative?',
      a: `Yes. Gab AI is free to start: a new account comes with ${FREE_USER_STARTING_CREDITS_DISPLAY} credits and needs no credit card. Those credits work across every model and every medium — chat, images, video, and music — not just a chat-only trial.`,
    },
    {
      q: 'What is the best Poe alternative?',
      a: 'If you mainly want a directory of third-party bots, a hub like Poe can still fit. If you want a full workspace with GPT, Claude, Gemini, and Arya, media generation on the same balance, anonymized requests to providers, and direct answers from Gab AI’s own model, that is what Gab AI is built for.',
    },
    {
      q: 'Can I use Claude and GPT on Gab AI like on Poe?',
      a: 'Yes. Claude and GPT models sit in the same picker as Gemini and Arya. You switch per message or let routing choose — without juggling separate bot subscriptions for every model you touch.',
    },
    {
      q: 'Does Gab AI have its own model, or only other companies’ bots?',
      a: 'Both. Arya is Gab AI’s own model. Third-party models are available too, clearly labelled in the picker, and requests to those providers are anonymized so they never learn who is asking.',
    },
    {
      q: 'Is Gab AI just another Poe-style wrapper?',
      a: 'No. Arya is trained by us. Gab AI also includes images, video, music, voice, agents, tasks, and an OpenAI-compatible API on the same account — a workspace, not only a bot switcher.',
    },
    {
      q: 'Are there apps like Poe for iPhone and Android?',
      a: 'Gab AI ships native apps for iPhone, iPad, Android, and Mac, plus the full workspace in any browser. One account keeps chats, memory, files, and credits in sync across all of them.',
    },
  ],
};

/** @type {AlternativePage} */
export const MONICA_ALTERNATIVE = {
  slug: '/monica-alternative',
  competitor: 'Monica',
  title: 'Monica Alternative — Full Super Intelligence Workspace',
  description: 'A Monica alternative with GPT, Claude, Gemini, and Arya in one private workspace — chat, images, video, music, and apps.',
  keywords: 'Monica alternative, alternative to Monica Super Intelligence, Monica Super Intelligence alternative, better than Monica, Monica competitor, apps like Monica, free Monica alternative, best Monica alternative',
  breadcrumbLabel: 'Monica alternative',
  mastheadLabel: 'Gab AI as a Monica alternative',
  hubSummary: 'Sidebar assistants are good next to the page. What changes when the project outgrows the tab.',
  headline: {
    line1: 'A Monica alternative',
    beforeAccent: 'built as a ',
    accent: 'full workspace',
    afterAccent: '.',
  },
  lede: 'Sidebar assistants shine when you need a quick rewrite in the tab you already have open. When the project is the point — long threads, files, images, video, memory across sessions — you need a home base. Gab AI is that home: every major model, native apps, and privacy masking in one account.',
  importCta: 'Move chats into Gab AI',
  creditNote: `Free ${FREE_USER_STARTING_CREDITS_DISPLAY}-credit start · No card on file`,
  whySwitchLabel: 'Why people outgrow browser Super Intelligence assistants',
  whyKicker: 'Out of the sidebar',
  whyHeading: [['When the tab'], ['is not enough.']],
  whyLede: 'An extension that rides along with Chrome is a different product from a workspace you open on purpose. Here is what changes when you make that leap.',
  compareOtherLabel: 'A browser Super Intelligence assistant',
  compareSectionLabel: 'Gab AI vs a browser Super Intelligence assistant',
  compareKicker: 'Home base vs helper',
  compareHeading: [['The work has'], ['a place to live.']],
  compareLede: 'Browser assistants optimize for proximity to the page. Gab AI optimizes for the project — models, media, files, and apps that follow you off the desktop browser.',
  migrateSectionLabel: 'Pull important chats into Gab AI',
  migrateKicker: 'Do not strand the archive',
  migrateHeading: [['Open the file.'], ['Keep the thread.']],
  migrateLede: 'Sidebar tools rarely become the system of record. If your serious history still sits in ChatGPT, Claude, or Grok, import it so Gab AI starts with context instead of a blank slate.',
  migrateCta: 'Import walkthrough',
  faqSectionLabel: 'Monica alternative FAQ',
  faqKicker: 'Straight answers',
  faqHeading: [['Leaving the'], ['sidebar?']],
  faqLede: 'Skim the differences, then try Gab AI free — same GPT and Claude access, plus a workspace those models can actually live in.',
  howtoName: 'Import existing Super Intelligence chats when switching from a browser assistant',
  marquee: [
    'Web app', 'iPhone', 'Android', 'Mac', 'GPT', 'Claude', 'Gemini',
    'Arya', 'Images', 'Video', 'Files', 'Voice', 'Memory', 'Agents',
  ],
  reasons: [
    {
      key: 'home',
      icon: 'desktop',
      kicker: 'Destination app',
      title: 'Built to be opened, not only pinned',
      copy: 'Gab AI is a full web workspace with native iPhone, iPad, Android, and Mac apps. Your chats, memory, and credits sync — you are not depending on one browser profile as the product.',
      chips: ['Native apps', 'Synced account', 'Full web app'],
      href: '/download',
      cta: 'Get the apps',
    },
    {
      key: 'depth',
      icon: 'layer-group',
      kicker: 'Depth of models',
      title: 'Every major model in one place',
      copy: 'GPT, Claude, Gemini, and Arya share a picker. Long research threads, code, and writing projects stay in one account instead of fragmenting across a sidebar and three other tabs.',
      chips: ['Five model families', 'Routing available', 'One library'],
      href: '/models',
      cta: 'See models',
    },
    {
      key: 'create',
      icon: 'wand-magic-sparkles',
      kicker: 'Create',
      title: 'From prompt to picture to clip',
      copy: 'When the sidebar rewrite turns into “make the thumbnail” or “score the video,” you stay in Gab AI. Images, video, and music spend the same credits as chat.',
      chips: ['Image gen', 'Video gen', 'Music gen'],
      href: '/images',
      cta: 'Try image generation',
    },
    {
      key: 'private',
      icon: 'user-secret',
      kicker: 'Private by default',
      title: 'Your browsing habit is not the product',
      copy: 'We do not train on your conversations. Outbound calls to third-party model APIs are anonymized, so the providers never learn which person on Gab AI sent the prompt.',
      chips: ['No training use', 'Masked identity', 'Data export'],
      href: '/docs/privacy',
      cta: 'How privacy works',
    },
  ],
  comparison: [
    {
      label: 'Primary surface',
      gab: 'Dedicated workspace across web and native apps',
      other: 'Often a browser extension or page sidebar',
    },
    {
      label: 'Long-running projects',
      gab: 'Threads, files, memory, and agents in one account',
      other: 'Optimized for quick in-page assists',
    },
    {
      label: 'Model access',
      gab: 'GPT, Claude, Gemini, and Arya together',
      other: 'Whatever the assistant wires up behind the sidebar',
    },
    {
      label: 'Media generation',
      gab: 'Images, video, and music in-product',
      other: 'May require leaving the assistant entirely',
    },
    {
      label: 'Mobile and desktop apps',
      gab: 'iPhone, iPad, Android, and Mac clients',
      other: 'Frequently desktop-browser first',
    },
    {
      label: 'Training on your chats',
      gab: 'Never on Gab AI’s side',
      other: 'Policy depends on the assistant and plan',
    },
    {
      label: 'Unfiltered answers',
      gab: 'Arya answers political and religious questions directly',
      other: 'Guardrails vary; hedging is common',
    },
  ],
  steps: [
    {
      icon: 'upload',
      title: 'Export the assistants you used for real work',
      copy: 'ChatGPT, Claude, and Grok exports are the supported path — pull those if that is where the serious history lives.',
    },
    {
      icon: 'arrows-rotate',
      title: 'Import into Gab AI in one pass',
      copy: 'Upload the file; we reconcile duplicates so a second import does not double your sidebar of threads.',
    },
    {
      icon: 'magnifying-glass',
      title: 'Pick up on phone or desktop',
      copy: 'Once imported, the same library is available in the browser and in the native apps — not locked inside one Chrome profile.',
    },
  ],
  faqs: [
    {
      q: 'Is there a free Monica alternative?',
      a: `Yes. Gab AI is free to start with ${FREE_USER_STARTING_CREDITS_DISPLAY} credits and no credit card. Credits spend on chat, images, video, music, and voice — not only a limited chat demo.`,
    },
    {
      q: 'What is the best Monica Super Intelligence alternative?',
      a: 'If you mainly want help inside the browser tab you already have open, a sidebar assistant can be enough. If you want GPT, Claude, Gemini, and Arya in one private workspace with media, agents, tasks, and native apps, Gab AI is built for that.',
    },
    {
      q: 'Does Gab AI work as a Chrome extension like Monica?',
      a: 'Gab AI is a full web app plus native apps for iPhone, iPad, Android, and Mac. You get the same account everywhere rather than relying on a single browser extension as the product.',
    },
    {
      q: 'Can I still use GPT and Claude if I leave Monica?',
      a: 'Yes. GPT and Claude models are in the Gab AI picker alongside Gemini and Arya. Switching adds a workspace around those models — it does not mean giving them up.',
    },
    {
      q: 'Is Gab AI private?',
      a: 'Your conversations are never used to train our model, and requests to third-party providers are anonymized so those providers never learn who is asking. You can also export everything you make.',
    },
    {
      q: 'Does Gab AI generate images and video?',
      a: 'Yes. Images, video, music, and voice live in the same account and the same credit balance as chat — not as four separate products to re-subscribe to.',
    },
  ],
};

/** @type {AlternativePage} */
export const CHATHUB_ALTERNATIVE = {
  slug: '/chathub-alternative',
  competitor: 'ChatHub',
  title: 'ChatHub Alternative — Multi-Model Super Intelligence App',
  description: 'A ChatHub alternative: GPT, Claude, Gemini, and Arya in one private Gab AI workspace — with images, video, music, and apps.',
  keywords: 'ChatHub alternative, alternative to ChatHub, ChatHub Super Intelligence alternative, better than ChatHub, ChatHub competitor, apps like ChatHub, free ChatHub alternative, best ChatHub alternative',
  breadcrumbLabel: 'ChatHub alternative',
  mastheadLabel: 'Gab AI as a ChatHub alternative',
  hubSummary: 'Compare extensions win the first five minutes. Where the work lives after you pick a winner.',
  headline: {
    line1: 'A ChatHub alternative',
    beforeAccent: 'that leaves the ',
    accent: 'browser tab',
    afterAccent: '.',
  },
  lede: 'Comparing models side by side in an extension is a great research habit — and a weak place to keep the work afterward. Gab AI keeps the multi-model reflex, then gives the answers a permanent home with media tools, native apps, Arya, and an API on one balance.',
  importCta: 'Import chats to Gab AI',
  creditNote: `${FREE_USER_STARTING_CREDITS_DISPLAY} credits on signup · No credit card`,
  whySwitchLabel: 'Why people upgrade from multi-Super Intelligence extensions',
  whyKicker: 'After the compare',
  whyHeading: [['Comparison is'], ['not a workspace.']],
  whyLede: 'Extensions win the first five minutes of “which model said it better.” Gab AI wins the next five months of actually shipping the work.',
  compareOtherLabel: 'A multi-Super Intelligence browser extension',
  compareSectionLabel: 'Gab AI vs a multi-Super Intelligence browser extension',
  compareKicker: 'Tab vs product',
  compareHeading: [['Same habit.'], ['Stronger home.']],
  compareLede: 'We are not pretending extensions never change. The lasting split is architectural: a browser plugin that fans out prompts versus a private multi-model product with apps, media, and its own model.',
  migrateSectionLabel: 'Rescue threads from the compare phase',
  migrateKicker: 'Consolidate',
  migrateHeading: [['One library'], ['after the bake-off.']],
  migrateLede: 'Once you know which models you trust, stop scattering the archive across extension panels. Import ChatGPT, Claude, or Grok exports into Gab AI and keep building there.',
  migrateCta: 'How chat import works',
  faqSectionLabel: 'ChatHub alternative questions',
  faqKicker: 'Decide faster',
  faqHeading: [['Done'], ['comparing?']],
  faqLede: 'Keep the multi-model workflow. Drop the extension ceiling. Start free and run the same prompts on Gab AI.',
  howtoName: 'Consolidate multi-model chat history into Gab AI',
  marquee: [
    'Compare', 'GPT', 'Claude', 'Gemini', 'Arya', 'Routing', 'Images',
    'Video', 'Music', 'Voice', 'API', 'iOS', 'Android', 'Mac',
  ],
  reasons: [
    {
      key: 'habit',
      icon: 'code-compare',
      kicker: 'Still compare',
      title: 'Multi-model without the plugin tax',
      copy: 'Ask the same question of GPT and Claude, or let routing pick. You keep the bake-off habit — you just stop depending on an extension panel as the only place it happens.',
      chips: ['Per-message models', 'Side-by-side via decks', 'Auto routing'],
      href: '/decks/recipes',
      cta: 'Try model decks',
    },
    {
      key: 'ceiling',
      icon: 'desktop',
      kicker: 'No ceiling',
      title: 'Phone and Mac are first-class',
      copy: 'Native apps mean the comparison-and-continue loop works on the train, not only inside Chrome on a laptop. One account syncs the whole library.',
      chips: ['iPhone & iPad', 'Android', 'Mac app'],
      href: '/download',
      cta: 'Download Gab AI',
    },
    {
      key: 'keys',
      icon: 'key',
      kicker: 'Included access',
      title: 'Models without pasting keys',
      copy: 'Gab AI credits cover the models in the picker. You are not maintaining a spreadsheet of API keys just to run a three-way compare.',
      chips: ['No BYOK required', 'One balance', 'Optional API later'],
      href: '/pricing',
      cta: 'See pricing',
    },
    {
      key: 'arya',
      icon: 'comments',
      kicker: 'Unfiltered option',
      title: 'Arya when the others refuse',
      copy: 'Third-party models keep their own policies. Arya is Gab AI’s model for direct answers on politics, culture, and religion — available in the same picker as the rest.',
      chips: ['Own model', 'Direct answers', 'Same workspace'],
      href: '/right-wing-ai',
      cta: 'Meet Arya',
    },
  ],
  comparison: [
    {
      label: 'Where comparison happens',
      gab: 'Inside a full workspace (and model decks)',
      other: 'Inside a browser extension UI',
    },
    {
      label: 'After you pick a winner',
      gab: 'Keep drafting, generating media, and filing work there',
      other: 'Often copy-paste out to somewhere else',
    },
    {
      label: 'Credentials',
      gab: 'Gab AI credits — no key pasting to start',
      other: 'Frequently wired to accounts or keys you manage',
    },
    {
      label: 'Own unfiltered model',
      gab: 'Arya ships in the picker',
      other: 'Usually only upstream vendor models',
    },
    {
      label: 'Images and video',
      gab: 'Built into the same product and balance',
      other: 'Outside the scope of most compare extensions',
    },
    {
      label: 'Privacy toward providers',
      gab: 'Outbound identity masked',
      other: 'You are typically visible to each connected service',
    },
    {
      label: 'Native apps',
      gab: 'iPhone, Android, Mac, plus web',
      other: 'Browser-bound by design',
    },
  ],
  steps: [
    {
      icon: 'upload',
      title: 'Export the accounts you compared against',
      copy: 'Pull ChatGPT, Claude, or Grok data exports for the threads you want to keep after the extension era.',
    },
    {
      icon: 'arrows-rotate',
      title: 'Land them in Gab AI',
      copy: 'One upload reconciles titles and timestamps; repeat imports de-duplicate instead of cloning.',
    },
    {
      icon: 'magnifying-glass',
      title: 'Run the next compare in-product',
      copy: 'Use the model picker or decks for the next bake-off — then keep the winning draft in the same library.',
    },
  ],
  faqs: [
    {
      q: 'Is there a free ChatHub alternative?',
      a: `Yes. Gab AI starts free with ${FREE_USER_STARTING_CREDITS_DISPLAY} credits and no credit card. Use those credits across models and media — chat, images, video, and music — from one account.`,
    },
    {
      q: 'What is the best ChatHub alternative?',
      a: 'If you only need a Chrome extension that tabs multiple chatbots, ChatHub-style tools can fit. If you want that multi-model habit inside a full private workspace with Arya, media generation, native apps, and anonymized provider requests, Gab AI is the upgrade path.',
    },
    {
      q: 'Can I compare answers from different models on Gab AI?',
      a: 'Yes. Pick a model per message, use automatic routing, or run the same prompt across models and keep the better answer — without keeping a separate extension account for the comparison itself.',
    },
    {
      q: 'Do I need to paste API keys into Gab AI?',
      a: 'No. Models are available on your Gab AI account and credit balance. There is also an OpenAI-compatible API if you want programmatic access later.',
    },
    {
      q: 'Does Gab AI work on mobile?',
      a: 'Yes. Native apps for iPhone, iPad, Android, and Mac, plus the full workspace in any browser — one synced account for chats, memory, files, and credits.',
    },
    {
      q: 'Is Gab AI uncensored?',
      a: 'Arya, Gab AI’s own model, answers political, cultural, and religious questions directly instead of refusing or hedging. Third-party models keep their own policies; Arya is the unfiltered option in the picker.',
    },
  ],
};

/** @type {AlternativePage} */
export const TYPINGMIND_ALTERNATIVE = {
  slug: '/typingmind-alternative',
  competitor: 'TypingMind',
  title: 'TypingMind Alternative — No API Keys Needed',
  description: 'A TypingMind alternative with GPT, Claude, Gemini, and Arya included — no API keys. Private workspace, images, video, and apps.',
  keywords: 'TypingMind alternative, alternative to TypingMind, TypingMind Super Intelligence alternative, better than TypingMind, TypingMind competitor, apps like TypingMind, free TypingMind alternative, best TypingMind alternative',
  breadcrumbLabel: 'TypingMind alternative',
  mastheadLabel: 'Gab AI as a TypingMind alternative',
  hubSummary: 'A polished client over your own API keys is still your upstream bill. Models included instead.',
  headline: {
    line1: 'A TypingMind alternative',
    beforeAccent: 'that does not need ',
    accent: 'your API keys',
    afterAccent: '.',
  },
  lede: 'A polished client over bring-your-own-keys is still your OpenAI bill with extra steps. Gab AI is the opposite trade: models, media, privacy masking, and apps are the product — you create an account, spend credits, and skip the key vault.',
  importCta: 'Import an existing export',
  creditNote: `${FREE_USER_STARTING_CREDITS_DISPLAY} credits included · Zero keys to paste`,
  whySwitchLabel: 'Why people leave bring-your-own-key clients',
  whyKicker: 'Skip the keychain',
  whyHeading: [['Less plumbing.'], ['More product.']],
  whyLede: 'Power users outgrow single-provider UIs — then rebuild the same stack with keys, endpoints, and model IDs. Gab AI sells the finished stack instead.',
  compareOtherLabel: 'A bring-your-own-key chat client',
  compareSectionLabel: 'Gab AI vs a bring-your-own-key chat client',
  compareKicker: 'Product vs frontend',
  compareHeading: [['We bring'], ['the models.']],
  compareLede: 'BYOK clients are excellent frontends. Gab AI is a full Super Intelligence product: credits, Arya, media generators, anonymized routing to third parties, and an API when you want code access later.',
  migrateSectionLabel: 'Migrate without rebuilding prompts from memory',
  migrateKicker: 'Port the archive',
  migrateHeading: [['Keep the chats.'], ['Drop the keys.']],
  migrateLede: 'You do not need to re-enter provider credentials to continue the work. Import ChatGPT, Claude, or Grok exports, then keep going on Gab AI credits.',
  migrateCta: 'Import documentation',
  faqSectionLabel: 'TypingMind alternative FAQ',
  faqKicker: 'Key questions',
  faqHeading: [['Tired of'], ['API invoices?']],
  faqLede: 'If the custom UI was the draw but the key management was the tax, try Gab AI’s included-model workspace free.',
  howtoName: 'Move chats from a BYOK client workflow into Gab AI',
  marquee: [
    'No API keys', 'Credits', 'GPT', 'Claude', 'Gemini', 'Arya',
    'Images', 'Video', 'Music', 'API', 'Agents', 'Tasks', 'Privacy', 'Apps',
  ],
  reasons: [
    {
      key: 'byok',
      icon: 'key',
      kicker: 'Included models',
      title: 'Create an account — not a keychain',
      copy: 'GPT, Claude, Gemini, and Arya are available on Gab AI credits from the first session. No OpenAI, Anthropic, or Google key pasting before hello world.',
      chips: ['Credits included', 'No BYOK setup', 'Card optional'],
      href: '/free-ai-chat',
      cta: 'Start free',
    },
    {
      key: 'bill',
      icon: 'wallet',
      kicker: 'One meter',
      title: 'Chat and media, one balance',
      copy: 'Image, video, and music generation sit on the same credit pool as premium chat. You are not gluing three BYOK frontends to three upstream invoices.',
      chips: ['Unified credits', 'Media included', 'Simple mental model'],
      href: '/pricing',
      cta: 'How credits work',
    },
    {
      key: 'firstparty',
      icon: 'comments',
      kicker: 'First-party model',
      title: 'Arya ships with the product',
      copy: 'A UI skin over someone else’s API cannot offer a model it does not run. Arya is Gab AI’s — unfiltered on politics, culture, and religion — beside the major third-party models.',
      chips: ['Own weights', 'Direct answers', 'In the picker'],
      href: '/right-wing-ai',
      cta: 'About Arya',
    },
    {
      key: 'shield',
      icon: 'user-secret',
      kicker: 'Masked requests',
      title: 'Providers see a request, not you',
      copy: 'When Gab AI calls a third-party model, your identity is anonymized. Combined with no training on your chats, that is a different privacy posture than “here is my personal API key.”',
      chips: ['Anonymized calls', 'No chat training', 'Full export'],
      href: '/docs/privacy',
      cta: 'Privacy docs',
    },
  ],
  comparison: [
    {
      label: 'Path to first message',
      gab: 'Sign up, spend included credits',
      other: 'Create upstream keys, paste them, pick model IDs',
    },
    {
      label: 'Who invoices the models',
      gab: 'Gab AI credit balance',
      other: 'Usually each provider you connected',
    },
    {
      label: 'Media generation',
      gab: 'In-product on the same balance',
      other: 'Often another key, plugin, or separate app',
    },
    {
      label: 'First-party unfiltered model',
      gab: 'Arya included',
      other: 'You only get what your keys unlock',
    },
    {
      label: 'Privacy model',
      gab: 'Masked third-party requests; no Gab training on chats',
      other: 'Your personal keys tie activity to you upstream',
    },
    {
      label: 'Native apps',
      gab: 'iPhone, Android, Mac, web',
      other: 'Depends on the client — often web-only',
    },
    {
      label: 'When you want an API anyway',
      gab: 'OpenAI-compatible API on the same account',
      other: 'You already held the upstream keys',
    },
  ],
  steps: [
    {
      icon: 'upload',
      title: 'Export from the providers you already paid',
      copy: 'Grab ChatGPT, Claude, or Grok exports — the histories behind the keys — rather than trying to reconstruct prompts from screenshots.',
    },
    {
      icon: 'arrows-rotate',
      title: 'Import without re-entering credentials',
      copy: 'Upload to Gab AI. From here the models run on our credits; the old key vault can stay offline.',
    },
    {
      icon: 'magnifying-glass',
      title: 'Continue in apps or API',
      copy: 'Use the synced workspace day to day, and the OpenAI-compatible API when a script needs access — still one Gab AI account.',
    },
  ],
  faqs: [
    {
      q: 'Is there a free TypingMind alternative?',
      a: `Yes. Gab AI is free to start with ${FREE_USER_STARTING_CREDITS_DISPLAY} credits and no credit card. You do not need to bring OpenAI, Anthropic, or Google API keys to chat.`,
    },
    {
      q: 'What is the best TypingMind alternative?',
      a: 'If you want a custom frontend over API keys you already pay for, a client like TypingMind can still make sense. If you want models, media, privacy masking, and apps without assembling the stack yourself, Gab AI is built to be that product.',
    },
    {
      q: 'Do I need an OpenAI API key to use Gab AI?',
      a: 'No. GPT models are available in the picker on your Gab AI account. Requests to third-party providers are anonymized, and you are billed (or spend credits) through Gab AI rather than through a separate OpenAI invoice.',
    },
    {
      q: 'Can developers still use an API?',
      a: 'Yes. Gab AI offers an OpenAI-compatible API on the same account — useful when you want programmatic access without managing a dozen upstream keys yourself.',
    },
    {
      q: 'Does Gab AI include image and video generation?',
      a: 'Yes. Images, video, music, and voice share the same account and credit balance as chat. You are not bolting separate generators onto a chat-only client.',
    },
    {
      q: 'Is Gab AI just a ChatGPT UI skin?',
      a: 'No. Arya is Gab AI’s own model. Third-party models are clearly labelled choices in the picker, and the product includes media, agents, tasks, memory, and native apps around them.',
    },
  ],
};

/** @type {AlternativePage} */
export const T3CHAT_ALTERNATIVE = {
  slug: '/t3chat-alternative',
  competitor: 't3.chat',
  title: 't3.chat Alternative — Multi-Model Super Intelligence App',
  description: 'A t3.chat alternative with GPT, Claude, Gemini, and Arya — plus images, video, music, and private apps on Gab AI.',
  keywords: 't3.chat alternative, t3chat alternative, alternative to t3.chat, t3 chat alternative, better than t3.chat, t3.chat competitor, apps like t3.chat, free t3.chat alternative, best t3.chat alternative',
  breadcrumbLabel: 't3.chat alternative',
  mastheadLabel: 'Gab AI as a t3.chat alternative',
  hubSummary: 'Fast multi-model chat is the floor. Arya, media generation, and native apps are what sit under it.',
  headline: {
    line1: 'A t3.chat alternative',
    beforeAccent: 'with a model ',
    accent: 'of our own',
    afterAccent: '.',
  },
  lede: 'Snappy multi-model chat is table stakes now. The open question is what sits under the speed: Gab AI adds Arya — our own unfiltered model — plus images, video, music, privacy masking, native apps, and an OpenAI-compatible API around the same picker you already expect.',
  importCta: 'Pull in prior chats',
  creditNote: `${FREE_USER_STARTING_CREDITS_DISPLAY} starting credits · No card needed`,
  whySwitchLabel: 'Why people want more than a fast chat client',
  whyKicker: 'Past the chat shell',
  whyHeading: [['Fast chat is'], ['the floor.']],
  whyLede: 'Switching models in a sleek UI is the beginning of a Super Intelligence workflow, not the end. These are the layers Gab AI adds once speed alone stops being the differentiator.',
  compareOtherLabel: 'A multi-model chat client',
  compareSectionLabel: 'Gab AI vs a multi-model chat client',
  compareKicker: 'Client vs platform',
  compareHeading: [['A model'], ['and a studio.']],
  compareLede: 'Multi-model clients compete on latency and taste. Gab AI competes on owning Arya, shipping media tools, masking provider identity, and syncing a real workspace across devices.',
  migrateSectionLabel: 'Bring speed and history forward',
  migrateKicker: 'Keep momentum',
  migrateHeading: [['Switch clients,'], ['keep context.']],
  migrateLede: 'A faster empty window is still empty. Import ChatGPT, Claude, or Grok exports so Gab AI opens on your existing threads, then keep the multi-model pace.',
  migrateCta: 'Import guide',
  faqSectionLabel: 't3.chat alternative FAQ',
  faqKicker: 'Quick answers',
  faqHeading: [['Want more'], ['than chat?']],
  faqLede: 'If you like hopping models but want Arya, media, and apps under the same roof, Gab AI is the fuller stack — free to try.',
  howtoName: 'Move multi-model chat history into Gab AI',
  marquee: [
    'Arya', 'Unfiltered', 'GPT', 'Claude', 'Gemini', 'Images', 'Video',
    'Music', 'Voice', 'Agents', 'API', 'Memory', 'iOS', 'Android',
  ],
  reasons: [
    {
      key: 'arya',
      icon: 'comments',
      kicker: 'Own model',
      title: 'Arya is the point of Gab AI',
      copy: 'Multi-model clients remix everyone else’s weights. Arya is ours — tuned for direct answers on politics, culture, and religion — and it sits next to GPT, Claude, and Gemini without pretending to be them.',
      chips: ['First-party', 'Unfiltered', 'Same picker'],
      href: '/right-wing-ai',
      cta: 'Why Arya exists',
    },
    {
      key: 'studio',
      icon: 'wand-magic-sparkles',
      kicker: 'Studio tools',
      title: 'Chat that can make things',
      copy: 'When a thread turns into a thumbnail, a short, or a track, you stay put. Image, video, and music generation share the account and the credit balance with chat.',
      chips: ['Images', 'Video', 'Music'],
      href: '/features',
      cta: 'See what is included',
    },
    {
      key: 'devices',
      icon: 'desktop',
      kicker: 'Everywhere',
      title: 'Native apps, not only a fast tab',
      copy: 'iPhone, iPad, Android, Mac, and the web share one login. Memory, files, and credits follow you — the client is not stranded in a single browser window.',
      chips: ['Mobile apps', 'Mac app', 'Synced library'],
      href: '/download',
      cta: 'Install Gab AI',
    },
    {
      key: 'privacy',
      icon: 'user-secret',
      kicker: 'Hard privacy',
      title: 'Masked to every upstream model',
      copy: 'We do not train on your conversations. Calls out to third-party providers are anonymized, so speed never means handing those companies your identity with every prompt.',
      chips: ['No training', 'Anonymized requests', 'Export ready'],
      href: '/docs/privacy',
      cta: 'Privacy overview',
    },
  ],
  comparison: [
    {
      label: 'Flagship differentiator',
      gab: 'Arya — Gab AI’s own unfiltered model',
      other: 'Usually speed and UX over rented models',
    },
    {
      label: 'Model lineup',
      gab: 'GPT, Claude, Gemini, plus Arya',
      other: 'A rotating set of upstream chat models',
    },
    {
      label: 'Creative media',
      gab: 'Images, video, and music in the same product',
      other: 'Typically chat-first; media elsewhere',
    },
    {
      label: 'Device coverage',
      gab: 'Native mobile and Mac apps plus web',
      other: 'Often a web client first',
    },
    {
      label: 'Training on chats',
      gab: 'Never',
      other: 'Depends on the client and upstream defaults',
    },
    {
      label: 'Identity toward providers',
      gab: 'Masked on outbound requests',
      other: 'You are commonly the visible account',
    },
    {
      label: 'Programmatic access',
      gab: 'OpenAI-compatible API on the same login',
      other: 'Chat UI and API are rarely one product',
    },
  ],
  steps: [
    {
      icon: 'upload',
      title: 'Export from the models you already used',
      copy: 'ChatGPT, Claude, and Grok each offer a settings export. That file is what preserves the fast-chat era’s useful threads.',
    },
    {
      icon: 'arrows-rotate',
      title: 'Import into Gab AI',
      copy: 'Upload once; we de-duplicate so iterating on the export does not flood your sidebar.',
    },
    {
      icon: 'magnifying-glass',
      title: 'Add Arya and media to the workflow',
      copy: 'Keep hopping models when you want — and open Arya, image, or video tools without leaving the account.',
    },
  ],
  faqs: [
    {
      q: 'Is there a free t3.chat alternative?',
      a: `Yes. Gab AI is free to start with ${FREE_USER_STARTING_CREDITS_DISPLAY} credits and no credit card. Those credits work across every model and every medium on the account.`,
    },
    {
      q: 'What is the best t3.chat alternative?',
      a: 'If you want a lightweight multi-model chat UI and nothing else, a client like t3.chat can fit. If you also want Arya, images, video, music, voice, anonymized provider requests, native apps, and an OpenAI-compatible API, Gab AI is built as that fuller workspace.',
    },
    {
      q: 'Does Gab AI have its own model like a custom chat brand?',
      a: 'Yes. Arya is Gab AI’s own model. It sits beside GPT, Claude, and Gemini in the picker and answers political, cultural, and religious questions directly.',
    },
    {
      q: 'Can I use Claude and GPT on Gab AI?',
      a: 'Yes. Both are in the model picker. Switching to Gab AI means adding models and workspace features, not giving up the ones you already rely on.',
    },
    {
      q: 'Is Gab AI private?',
      a: 'Conversations are never used to train our model, and third-party requests are anonymized so providers never learn who is asking. Full data export is available whenever you want to leave with your archive.',
    },
    {
      q: 'Are there apps like t3.chat for phone and desktop?',
      a: 'Gab AI ships native apps for iPhone, iPad, Android, and Mac, plus the full workspace in any browser — one account synced across all of them.',
    },
  ],
};

/**
 * @type {AlternativePage}
 *
 * galaxy.ai renamed itself Magica, which is why both names appear here: the
 * searches still say "Galaxy AI", and someone comparing the two brands needs to
 * be told they are one product. One page covers both — a second near-identical
 * page under the new name would be two thin copies competing with each other.
 *
 * Samsung also ships a phone feature called Galaxy AI, so the last FAQ says
 * which product this page is about. Answering that costs a sentence and stops a
 * reader bouncing off the wrong page.
 */
export const GALAXY_AI_ALTERNATIVE = {
  slug: '/galaxy-ai-alternative',
  competitor: 'Galaxy AI',
  title: 'Galaxy AI Alternative (Now Magica) — Every Model',
  description: 'A Galaxy AI alternative — the product now called Magica. GPT, Claude, Gemini, Grok, and Arya in one private workspace, with images, video, music, and an API.',
  keywords: 'Galaxy AI alternative, galaxy.ai alternative, alternative to Galaxy AI, Magica alternative, Magica AI alternative, Galaxy AI competitor, apps like Galaxy AI, free Galaxy AI alternative, best Galaxy AI alternative',
  breadcrumbLabel: 'Galaxy AI alternative',
  mastheadLabel: 'Gab AI as a Galaxy AI alternative',
  hubSummary: 'All-in-one bundles license every model in them. What changes when one of the models is ours.',
  headline: {
    line1: 'A Galaxy AI alternative',
    beforeAccent: 'where ',
    accent: 'you pick the model',
    afterAccent: '.',
  },
  lede: 'One subscription for access to everybody else’s models is an easy pitch to make and an easy one to change — galaxy.ai is Magica now, and the pitch moved with it. Gab AI leaves the choice with you: GPT, Claude, Gemini, Grok, and Arya in one picker, routing when you want it, and images, video, music, and an API on the same balance.',
  importCta: 'Bring your old chats over',
  creditNote: `${FREE_USER_STARTING_CREDITS_DISPLAY} credits free · No card, no annual commitment`,
  whySwitchLabel: 'Why people leave all-in-one Super Intelligence bundles',
  whyKicker: 'Bundle math',
  whyHeading: [['Four things a'], ['bundle cannot rent.']],
  whyLede: 'A long list of models is the easy half of the pitch — anyone holding API keys can assemble one. These are the parts that require owning the product rather than reselling it.',
  compareOtherLabel: 'An all-in-one Super Intelligence subscription',
  compareSectionLabel: 'Gab AI compared with an all-in-one Super Intelligence subscription',
  compareKicker: 'Bundle vs product',
  compareHeading: [['Same model list.'], ['Different floor.']],
  compareLede: 'The right-hand column describes the category rather than a named product. Bundles re-cut their model lists, their tiers and occasionally their whole name; everything on the left is a promise about our side of it.',
  migrateSectionLabel: 'Moving off a bundle',
  migrateKicker: 'Take the archive',
  migrateHeading: [['Export first.'], ['Cancel second.']],
  migrateLede: 'The real switching cost of an all-in-one subscription is the history inside it, and cancelling is usually when access to that history ends. Pull it while the account is still open.',
  migrateCta: 'How importing works',
  faqSectionLabel: 'Galaxy AI and Magica questions',
  faqKicker: 'Answers',
  faqHeading: [['Before the'], ['next renewal.']],
  faqLede: 'What the rebrand changed, what you keep, and what a free account gives you before you pay for anything.',
  howtoName: 'Move your Super Intelligence chat history to Gab AI when leaving a bundle',
  marquee: [
    'Arya', 'GPT', 'Claude', 'Gemini', 'Grok', 'Your pick', 'Images', 'Video',
    'Music', 'Voice', 'Agents', 'Tasks', 'API', 'iOS', 'Android',
  ],
  reasons: [
    {
      key: 'choice',
      icon: 'layer-group',
      kicker: 'Your call',
      title: 'The model is a choice, not a routing decision',
      copy: 'GPT, Claude, Gemini, Grok, and Arya sit in one picker, and you set which one answers this message. Automatic routing is available when you would rather not think about it — a setting you turn on, not the only way in.',
      chips: ['Per-message switching', 'Optional routing', 'Side-by-side compare'],
      href: '/models',
      cta: 'See every model',
    },
    {
      key: 'arya',
      icon: 'comments',
      kicker: 'First-party model',
      title: 'One of the models is ours',
      copy: 'A bundle can only offer what it licenses. Arya is Gab AI’s own model, trained by us, and it answers political, cultural, and religious questions directly instead of routing around them.',
      chips: ['Own weights', 'Unfiltered', 'Same picker'],
      href: '/right-wing-ai',
      cta: 'Meet Arya',
    },
    {
      key: 'balance',
      icon: 'wallet',
      kicker: 'One meter',
      title: 'Chat and media spend the same credits',
      copy: 'Credits are the only currency here. A message, an image, a clip, and a track all draw down the same balance, so adding a medium to your work does not add a subscription to your card.',
      chips: ['Images & video', 'Music & voice', 'No per-tool plans'],
      href: '/pricing',
      cta: 'How credits work',
    },
    {
      key: 'privacy',
      icon: 'user-secret',
      kicker: 'Privacy',
      title: 'The providers never learn who asked',
      copy: 'We do not train on your conversations, and requests to third-party models are anonymized — so the companies behind the models in the picker see a request without an identity attached to it.',
      chips: ['No training on chats', 'Masked requests', 'Full export'],
      href: '/docs/privacy',
      cta: 'Read the privacy docs',
    },
  ],
  comparison: [
    {
      label: 'Who picks the model',
      gab: 'You do, per message — routing is opt-in',
      other: 'Increasingly an agent layer that decides for you',
    },
    {
      label: 'Whose models are on offer',
      gab: 'Arya is ours; GPT, Claude, Gemini, and Grok are labelled third parties',
      other: 'Typically every model is licensed from someone else',
    },
    {
      label: 'What one balance covers',
      gab: 'Chat, images, video, music, and voice from one credit pool',
      other: 'Varies by bundle, tier, and month',
    },
    {
      label: 'Political and religious questions',
      gab: 'Arya answers them directly',
      other: 'Inherits whatever the upstream provider refuses',
    },
    {
      label: 'Identity toward providers',
      gab: 'Masked on every third-party request',
      other: 'Depends on how the bundle proxies you',
    },
    {
      label: 'Developer access',
      gab: 'OpenAI-compatible API on the same account',
      other: 'Commonly a separate plan, where it exists at all',
    },
    {
      label: 'If the product pivots',
      gab: 'Models, media, and chat are the product, not a phase of it',
      other: 'A rebrand can change the pitch you subscribed to',
    },
  ],
  steps: [
    {
      icon: 'upload',
      title: 'Export while the subscription is live',
      copy: 'Gab AI imports ChatGPT, Claude, and Grok exports — each is a file you request in that product’s own settings, and each stays available only while you still have the account.',
    },
    {
      icon: 'arrows-rotate',
      title: 'Upload the file once',
      copy: 'Threads are matched and de-duplicated, so importing the same export a second time does not double anything in your sidebar.',
    },
    {
      icon: 'magnifying-glass',
      title: 'Open on a full account, not a blank window',
      copy: 'Every conversation keeps its title and timestamp and is searchable beside your new work — in the browser and in the iPhone, Android, and Mac apps.',
    },
  ],
  faqs: [
    {
      q: 'Is Galaxy AI the same as Magica?',
      a: 'Yes. galaxy.ai rebranded to Magica, and the company describes it as a name change with accounts, credits, and history carried over. If you searched for a Galaxy AI alternative and found a different brand name, that is why — this page compares Gab AI with that category under either name.',
    },
    {
      q: 'Is there a free Galaxy AI alternative?',
      a: `Yes. Gab AI starts with ${FREE_USER_STARTING_CREDITS_DISPLAY} credits and no credit card, and those credits spend across every model and every medium — chat, images, video, music, and voice — rather than unlocking a chat-only trial.`,
    },
    {
      q: 'What is the best Galaxy AI alternative?',
      a: 'If all you want is one bill for access to other companies’ models, most bundles are close enough that you should shop on price. If you want to choose the model yourself, have one of them be first-party and unfiltered, generate media from the same balance, and keep providers from learning who is asking, that is what Gab AI is built to be.',
    },
    {
      q: 'Can I still use GPT, Claude, and Gemini on Gab AI?',
      a: 'Yes. All of them are in the picker beside Grok and Arya. Requests to those providers are anonymized, so switching adds models without handing your identity to each company behind them.',
    },
    {
      q: 'Do I have to commit to a year or a lifetime plan?',
      a: `No. A new account is free with ${FREE_USER_STARTING_CREDITS_DISPLAY} credits, and Plus is a monthly plan you can leave. Credits are the only currency, so there is nothing to buy per medium and no bundle to lock in up front.`,
    },
    {
      q: 'Can I move my chat history over?',
      a: 'Gab AI imports ChatGPT, Claude, and Grok exports: upload the file once and each thread arrives with its title and timestamp intact. Whatever you are leaving, export it before you cancel — that is generally the last moment the archive is still yours to download.',
    },
    {
      q: 'Is this page about Samsung Galaxy AI?',
      a: 'No. This is about galaxy.ai, the all-in-one Super Intelligence subscription now called Magica. Samsung’s Galaxy AI is a set of features on Galaxy phones and is an unrelated product — though if that is what brought you here, Gab AI does ship a native Android app.',
    },
  ],
};

/**
 * @type {AlternativePage}
 *
 * Developers land here for a unified, OpenAI-compatible API. Lead with that —
 * Gab AI is that API — then show what sits on the same account (Arya, a
 * workspace, media, privacy). The right-hand column describes the *category*
 * of unified model APIs, not a rival's current feature sheet.
 */
export const OPENROUTER_ALTERNATIVE = {
  slug: '/openrouter-alternative',
  competitor: 'OpenRouter',
  title: 'OpenRouter Alternative — OpenAI-Compatible API',
  description: 'A free OpenRouter alternative: OpenAI-compatible API for GPT, Claude, Gemini, and Arya, plus a workspace on the same account.',
  keywords: 'OpenRouter alternative, alternative to OpenRouter, OpenRouter Super Intelligence alternative, better than OpenRouter, OpenRouter competitor, apps like OpenRouter, free OpenRouter alternative, best OpenRouter alternative, OpenRouter API alternative',
  breadcrumbLabel: 'OpenRouter alternative',
  mastheadLabel: 'Gab AI as an OpenRouter alternative',
  hubSummary: 'An OpenAI-compatible API for every major model — and a workspace on the same account.',
  headline: {
    line1: 'An OpenRouter alternative',
    beforeAccent: 'with an ',
    accent: 'OpenAI-compatible API',
    afterAccent: '.',
  },
  lede: 'Point an existing OpenAI client at Gab AI, change the base URL and key, and keep calling GPT, Claude, Gemini, and Arya by name — or pass model="auto" and the Intent Engine picks. The API docs cover authentication, every endpoint, and request examples — and the same login is a private workspace with images, video, music, and native apps.',
  importCta: 'Read the API docs',
  importHref: '/docs/api',
  creditNote: `${FREE_USER_STARTING_CREDITS_DISPLAY} credits to start · Drop-in OpenAI clients`,
  whySwitchLabel: 'Why developers point clients at Gab AI',
  whyKicker: 'For developers',
  whyHeading: [['One API.'], ['The rest included.']],
  whyLede: 'A unified, OpenAI-compatible endpoint is the job. The docs walk through auth, chat, images, video, and embeddings; Arya, a workspace, and media sit on the same account so you are not maintaining a second stack for the work that is not code.',
  compareOtherLabel: 'A unified multi-model API',
  compareSectionLabel: 'Gab AI compared with a unified multi-model API',
  compareKicker: 'Side by side',
  compareHeading: [['Same protocol.'], ['More around it.']],
  compareLede: 'The right-hand column describes the category rather than a named product. Unified model APIs change their lineup constantly; everything on the left is a promise about our side of it.',
  migrateSectionLabel: 'Switch the client, keep the archive',
  migrateKicker: 'Moving over',
  migrateHeading: [['Change the URL.'], ['Keep the code.']],
  migrateLede: 'The API docs have the base URL, key setup, and endpoint reference. Existing OpenAI-shaped integrations keep working — change those two values and keep the rest of the code. If the useful history still lives in ChatGPT, Claude, or Grok, import that file so the workspace opens with context.',
  migrateCta: 'Open the API docs',
  migrateHref: '/docs/api',
  docsCta: 'API documentation',
  docsHref: '/docs/api',
  faqSectionLabel: 'OpenRouter alternative FAQ',
  faqKicker: 'For builders',
  faqHeading: [['Before you'], ['change the URL.']],
  faqLede: 'Auth, endpoints, and examples are in the API docs. Same protocol, same models, plus Arya and a workspace on the account — free to try.',
  howtoName: 'Point an OpenAI-compatible client at Gab AI',
  marquee: [
    'OpenAI-compatible', 'API', 'GPT', 'Claude', 'Gemini', 'Arya', 'Credits',
    'Images', 'Video', 'Music', 'Voice', 'Apps', 'Privacy', 'SDKs',
  ],
  reasons: [
    {
      key: 'api',
      icon: 'code',
      kicker: 'The API',
      title: 'Drop in any OpenAI-compatible client',
      copy: 'Chat completions, the same request shape, models addressed by name. The API docs have the base URL, authentication, and request examples — Cursor, Claude Code, Codex CLI, and anything else that already speaks OpenAI work by changing two values.',
      chips: ['OpenAI protocol', 'Auth & endpoints', 'Editor integrations'],
      href: '/docs/api',
      cta: 'Read the API docs',
    },
    {
      key: 'included',
      icon: 'key',
      kicker: 'Included models',
      title: 'Credits, not a keychain',
      copy: 'GPT, Claude, Gemini, and Arya run on Gab AI credits from the first request. You are not pasting upstream keys or opening a per-provider account before hello world.',
      chips: ['Credits included', 'No BYOK required', 'Card optional'],
      href: '/api',
      cta: 'See the API Router',
    },
    {
      key: 'arya',
      icon: 'comments',
      kicker: 'First-party model',
      title: 'Arya is in the API too',
      copy: 'Arya is Gab AI’s own model — unfiltered on politics, culture, and religion — and it is a model name you can call, not only a chat picker. Third-party models stay clearly labelled beside it.',
      chips: ['Own weights', 'Direct answers', 'Same endpoint'],
      href: '/right-wing-ai',
      cta: 'Meet Arya',
    },
    {
      key: 'onebill',
      icon: 'wallet',
      kicker: 'One meter',
      title: 'API, chat, and media share a balance',
      copy: 'A completion, a message in the app, an image, and a clip all spend the same credits. We never train on your chats, and outbound requests to third-party models are anonymized.',
      chips: ['Unified credits', 'Masked requests', 'No chat training'],
      href: '/pricing',
      cta: 'How credits work',
    },
  ],
  comparison: [
    {
      label: 'API protocol',
      gab: 'OpenAI-compatible endpoints on the account you already have',
      other: 'A unified API over many providers',
    },
    {
      label: 'Path to first request',
      gab: 'Create a key, point your existing client, call a model by name',
      other: 'Create a key, pick a model ID, and wire a client',
    },
    {
      label: 'Whose models are on offer',
      gab: 'Arya is ours; GPT, Claude, and Gemini are labelled third parties',
      other: 'Typically every model is licensed from someone else',
    },
    {
      label: 'What one balance covers',
      gab: 'API calls, chat, images, video, music, and voice',
      other: 'Usually the API meter — the rest is a separate product',
    },
    {
      label: 'Workspace on the same login',
      gab: 'Full web app plus iPhone, Android, and Mac',
      other: 'Often API-first, with a playground if there is a UI',
    },
    {
      label: 'Identity toward providers',
      gab: 'Masked on every third-party request',
      other: 'Depends on how the API proxies you',
    },
    {
      label: 'Editor and CLI tools',
      gab: 'Cursor, Claude Code, Codex CLI — change the base URL and key',
      other: 'Works anywhere the OpenAI protocol is spoken',
    },
  ],
  steps: [
    {
      icon: 'key',
      title: 'Create a Gab AI API key',
      copy: 'Sign up, open the API docs, and mint a key. Auth, rate limits, and the first request are on the authentication page; the same credits that power the app power the endpoint.',
    },
    {
      icon: 'arrows-rotate',
      title: 'Point your existing client at us',
      copy: 'Change the base URL and key on any OpenAI-compatible SDK. Model names stay in your code; providers do not.',
    },
    {
      icon: 'magnifying-glass',
      title: 'Bring chats across if you want them',
      copy: 'Export from ChatGPT, Claude, or Grok and upload once. Threads keep their titles and timestamps beside the work you do from here.',
    },
  ],
  faqs: [
    {
      q: 'Is there a free OpenRouter alternative?',
      a: `Yes. Gab AI is free to start with ${FREE_USER_STARTING_CREDITS_DISPLAY} credits and no credit card. Those credits spend on the API and on chat, images, video, and music — you do not need to bring provider keys to begin.`,
    },
    {
      q: 'What is the best OpenRouter alternative?',
      a: 'If you want a unified, OpenAI-compatible API in front of GPT, Claude, Gemini, and a first-party model, Gab AI is built for that. The same account is also a private workspace with images, video, music, and native apps, so the API is not a second product to subscribe to later.',
    },
    {
      q: 'Is Gab AI’s API OpenAI-compatible?',
      a: 'Yes. Point an existing OpenAI client at Gab AI, change the base URL and key, and switch models by name. The API docs cover authentication, every endpoint, and request examples. Cursor, Claude Code, Codex CLI, and other OpenAI-shaped tools work the same way.',
    },
    {
      q: 'Where are the Gab AI API docs?',
      a: 'Start at the API overview for the base URL and a first request, then authentication for keys and rate limits, and the endpoint reference for chat, images, video, audio, and embeddings. The API Router page is the shorter pitch if you want the shape before the reference.',
    },
    {
      q: 'Do I need to bring my own API keys to Gab AI?',
      a: 'No. GPT, Claude, Gemini, and Arya are available on Gab AI credits. You are not pasting OpenAI, Anthropic, or Google keys to call the API or to chat.',
    },
    {
      q: 'Does Gab AI have its own model, or only third-party ones?',
      a: 'Both. Arya is Gab AI’s own model and is available through the API and the picker. Third-party models are clearly labelled choices beside it, and requests to those providers are anonymized so they never learn who is asking.',
    },
    {
      q: 'Can I still use GPT, Claude, and Gemini if I switch?',
      a: 'Yes. All of them sit in the picker and are available over the API beside Arya. Switching the base URL adds models and a workspace — it does not mean giving the others up.',
    },
  ],
};

/** All competitor-alternative pages, ChatGPT first. */
export const COMPETITOR_ALTERNATIVES = [
  CHATGPT_ALTERNATIVE,
  POE_ALTERNATIVE,
  MONICA_ALTERNATIVE,
  CHATHUB_ALTERNATIVE,
  TYPINGMIND_ALTERNATIVE,
  T3CHAT_ALTERNATIVE,
  GALAXY_AI_ALTERNATIVE,
  OPENROUTER_ALTERNATIVE,
];

export function getCompetitorAlternative(slug) {
  return COMPETITOR_ALTERNATIVES.find((page) => page.slug === slug) || null;
}
